import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { CheckCircle2, Clock, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';
import { crmFollowupsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { formatDate, isKRRole } from '@/lib/utils';
import { toast } from 'sonner';

const STATUS_COLORS = {
  open: 'bg-slate-100 text-slate-700',
  due_soon: 'bg-amber-100 text-amber-800',
  overdue: 'bg-red-100 text-red-800',
  completed: 'bg-emerald-100 text-emerald-800',
};

function sourceLinkFor(fu) {
  if (!fu?.source_visible_to_viewer) return null;
  if (fu.source_type === 'contract_request' && fu.source_id) return `/requests/${fu.source_id}`;
  if (fu.source_type === 'contract_note' && fu.source_parent_id) return `/requests/${fu.source_parent_id}`;
  if (fu.source_type === 'crm_note' && fu.subject_type && fu.subject_id) {
    return fu.subject_type === 'agent' ? `/agents/${fu.subject_id}` : `/agencies/${fu.subject_id}`;
  }
  return null;
}

export function FollowupDetailDrawer({ open, onOpenChange, followupId, onChanged }) {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [fu, setFu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [busy, setBusy] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [completeComment, setCompleteComment] = useState('');
  const [completeResult, setCompleteResult] = useState('');
  const [newDue, setNewDue] = useState('');

  const load = async () => {
    if (!followupId) return;
    setLoading(true);
    try {
      const { data } = await crmFollowupsAPI.get(followupId);
      setFu(data);
      setNewDue(data.due_date || '');
    } catch (e) {
      toast.error('Failed to load follow-up');
      onOpenChange(false);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open, followupId]);

  const addProgress = async () => {
    if (!progress.trim()) return;
    setBusy(true);
    try {
      const { data } = await crmFollowupsAPI.addProgress(fu.followup_id, progress.trim());
      setFu(data); setProgress('');
      toast.success('Progress added'); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add progress');
    } finally { setBusy(false); }
  };

  const saveDue = async () => {
    if (!newDue || newDue === fu.due_date) return;
    setBusy(true);
    try {
      const { data } = await crmFollowupsAPI.patch(fu.followup_id, { due_date: newDue });
      setFu(data); toast.success('Due date updated'); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setBusy(false); }
  };

  const doComplete = async () => {
    if (!completeComment.trim() || !completeResult.trim()) {
      toast.error('Comment and result are required');
      return;
    }
    setBusy(true);
    try {
      const { data } = await crmFollowupsAPI.complete(fu.followup_id, {
        completion_comment: completeComment.trim(),
        completion_result: completeResult.trim(),
      });
      setFu(data);
      setShowComplete(false);
      toast.success('Follow-up completed');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to complete');
    } finally { setBusy(false); }
  };

  const deleteProgress = async (updateId) => {
    const reason = window.prompt('Reason for deleting this progress entry (min 5 chars)?');
    if (!reason || reason.trim().length < 5) return;
    setBusy(true);
    try {
      await crmFollowupsAPI.deleteProgress(fu.followup_id, updateId, reason.trim());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete progress');
    } finally { setBusy(false); }
  };

  const link = sourceLinkFor(fu);
  const closed = !!fu?.closed_at;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto"
                    data-testid="followup-detail-drawer">
        <SheetHeader>
          <SheetTitle>Follow-up Detail</SheetTitle>
          <SheetDescription>
            {fu ? `Source: ${fu.source_type}` : 'Loading…'}
          </SheetDescription>
        </SheetHeader>

        {loading || !fu ? (
          <div className="py-16 text-center text-slate-400">
            <Loader2 className="mx-auto animate-spin" />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={STATUS_COLORS[fu.status]}>{fu.status.replace('_', ' ')}</Badge>
                {fu.due_date && <span className="text-xs text-slate-500">Due {fu.due_date}</span>}
              </div>
              {link && (
                <a href={link} className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1">
                  Open source <ExternalLink size={12} />
                </a>
              )}
            </div>

            <div>
              <div className="text-lg font-semibold text-slate-900">{fu.title}</div>
              {fu.description && (
                <div className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{fu.description}</div>
              )}
              <div className="text-xs text-slate-500 mt-2">
                Created by {fu.created_by_name} · {formatDate(fu.created_at)}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Assignees</div>
              <div className="flex flex-wrap gap-2">
                {(fu.assignees || []).map((a, i) => (
                  <Badge key={i} variant="outline">
                    {a.kind === 'agency' ? '🏢' : '👤'} {a.display_name}
                    {a.kind === 'agency' && a.resolved_user_ids?.length ? (
                      <span className="ml-1 text-[10px] text-slate-500">({a.resolved_user_ids.length})</span>
                    ) : null}
                  </Badge>
                ))}
              </div>
            </div>

            {!closed && (
              <div className="rounded-md border border-slate-200 p-3 space-y-2 bg-slate-50">
                <div className="text-xs font-medium text-slate-700">Add progress update</div>
                <Textarea rows={2} value={progress} onChange={(e) => setProgress(e.target.value)}
                          placeholder="Text-only. No attachments." data-testid="followup-progress-input" />
                <div className="flex justify-end">
                  <Button size="sm" onClick={addProgress} disabled={busy || !progress.trim()}
                          data-testid="followup-progress-submit">
                    Post update
                  </Button>
                </div>
              </div>
            )}

            <div data-testid="followup-progress-list">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Progress history</div>
              {(fu.progress_history || []).length === 0 ? (
                <div className="text-sm text-slate-400">No progress yet.</div>
              ) : (
                <ul className="space-y-2">
                  {fu.progress_history.map((p) => (
                    <li key={p.update_id} className={`text-sm rounded-md border p-2 ${p.deleted_at ? 'opacity-60 border-dashed' : 'border-slate-200'}`}>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{p.author_name} · {formatDate(p.created_at)}</span>
                        {isKR && !p.deleted_at && (
                          <button className="text-red-500 hover:text-red-600" onClick={() => deleteProgress(p.update_id)}>Delete</button>
                        )}
                      </div>
                      {p.deleted_at ? (
                        <div className="text-xs italic text-slate-500">
                          Deleted {formatDate(p.deleted_at)} by {p.deleted_by_name}
                          {isKR && p.deletion_reason && <> — {p.deletion_reason}</>}
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap text-slate-800">{p.text}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {!closed && (
              <div className="rounded-md border border-slate-200 p-3 space-y-2">
                <div className="text-xs font-medium text-slate-700">Change due date</div>
                <div className="flex gap-2">
                  <Input type="date" value={newDue} onChange={(e) => setNewDue(e.target.value)} />
                  <Button size="sm" variant="outline" onClick={saveDue} disabled={busy || !newDue || newDue === fu.due_date}
                          data-testid="followup-change-due-btn">
                    Save
                  </Button>
                </div>
              </div>
            )}

            {!closed && (
              <div className="pt-2 border-t border-slate-200">
                <Button className="w-full" onClick={() => setShowComplete(true)}
                        data-testid="followup-complete-btn">
                  <CheckCircle2 size={14} className="mr-1" /> Complete Follow-up
                </Button>
              </div>
            )}

            {closed && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
                <div className="text-xs uppercase tracking-wide text-emerald-700 mb-1">Completion</div>
                <div><b>Result:</b> {fu.completion_result}</div>
                <div className="text-slate-700 whitespace-pre-wrap"><b>Comment:</b> {fu.completion_comment}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Closed by {fu.closed_by_name} · {formatDate(fu.closed_at)}
                </div>
              </div>
            )}

            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Change history</div>
              <ul className="space-y-1 text-xs text-slate-600">
                {(fu.change_history || []).map((c) => (
                  <li key={c.change_id}>
                    <span className="text-slate-500">{formatDate(c.at)}</span>
                    {' — '}<b>{c.actor_name}</b> {c.action.replace(/_/g, ' ')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <Dialog open={showComplete} onOpenChange={setShowComplete}>
          <DialogContent data-testid="followup-complete-dialog">
            <DialogHeader>
              <DialogTitle>Complete Follow-up</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Result *</Label>
                <Input value={completeResult} onChange={(e) => setCompleteResult(e.target.value)}
                       placeholder="e.g. Contract signed" data-testid="followup-complete-result" />
              </div>
              <div>
                <Label>Comment *</Label>
                <Textarea rows={4} value={completeComment} onChange={(e) => setCompleteComment(e.target.value)}
                          placeholder="Describe the outcome" data-testid="followup-complete-comment" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowComplete(false)} disabled={busy}>Cancel</Button>
              <Button onClick={doComplete} disabled={busy} data-testid="followup-complete-confirm">
                {busy ? 'Saving…' : 'Complete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

export default FollowupDetailDrawer;
